import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../store/useStore';
import { TopBar } from '../components/layout/TopBar';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Wallet, ArrowUpRight, ArrowDownLeft, Plus, Send, QrCode, History,
  Copy, Check, AlertTriangle, X, ChevronRight, RefreshCw, Eye, EyeOff,
  CreditCard, Building2, Bitcoin, DollarSign, Clock, CheckCircle2,
  XCircle, AlertCircle, Loader2, Shield, Search, Filter, ChevronDown
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ─── helpers ──────────────────────────────────
const fmt = (n) => new Intl.NumberFormat('es-MX', { style:'currency', currency:'MXN' }).format(n || 0);
const fmtDate = (d) => { try { return format(parseISO(d), "d 'de' MMM HH:mm", {locale:es}); } catch { return d; } };

function statusBadge(s) {
  const map = {
    completed: { color:'#22c55e', bg:'#f0fdf4', label:'Completado', Icon:CheckCircle2 },
    pending:   { color:'#f59e0b', bg:'#fffbeb', label:'Pendiente',  Icon:Clock },
    failed:    { color:'#ef4444', bg:'#fef2f2', label:'Fallido',    Icon:XCircle },
    disputed:  { color:'#8b5cf6', bg:'#f5f3ff', label:'Disputa',   Icon:AlertCircle },
    reversed:  { color:'#6b7280', bg:'#f9fafb', label:'Revertido', Icon:RefreshCw },
  };
  const m = map[s] || { color:'#9ca3af', bg:'#f9fafb', label: s, Icon: Clock };
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 10px', borderRadius:20, background:m.bg, color:m.color, fontSize:11, fontWeight:700 }}>
      <m.Icon size={10} /> {m.label}
    </span>
  );
}

function categoryIcon(cat) {
  const map = { recharge:'⬆️', transfer_in:'📥', transfer_out:'📤', fee:'💸', refund:'🔄', adjustment:'⚙️' };
  return map[cat] || '💱';
}

// ─── Modals ────────────────────────────────────
function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth: wide ? 600 : 440, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 24px', borderBottom:'1px solid #f3f4f6' }}>
          <h2 style={{ fontWeight:800, fontSize:17, margin:0, color:'#111827' }}>{title}</h2>
          <button onClick={onClose} style={{ background:'#f3f4f6', border:'none', borderRadius:8, padding:'6px 8px', cursor:'pointer', display:'flex' }}><X size={16}/></button>
        </div>
        <div style={{ padding:'20px 24px' }}>{children}</div>
      </div>
    </div>
  );
}

function RechargeModal({ wallet, onClose, onSuccess }) {
  const [method, setMethod] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null);

  const METHODS = [
    { id:'stripe', label:'Tarjeta de crédito / débito', icon:<CreditCard size={20} color="#6366f1"/>, fee:'2.9%', desc:'Visa, Mastercard, AMEX — Acreditación inmediata' },
    { id:'spei',   label:'SPEI / Transferencia bancaria', icon:<Building2 size={20} color="#22c55e"/>, fee:'$5.00', desc:'Desde cualquier banco — 1-2 horas hábiles' },
    { id:'crypto', label:'Criptomonedas', icon:<Bitcoin size={20} color="#f59e0b"/>, fee:'1.0%', desc:'BTC, ETH, USDT — Confirmación en ~30 min' },
    { id:'cash',   label:'Efectivo', icon:<DollarSign size={20} color="#10b981"/>, fee:'Gratis', desc:'Acércate al restaurante o punto autorizado' },
  ];

  const presets = [100, 200, 500, 1000, 2000];

  const submit = async () => {
    if (!method || !amount || parseFloat(amount) <= 0) return;
    setLoading(true);
    try {
      const res = await api.post('/wallet/recharge/initiate', { amount: parseFloat(amount), method });
      setResult(res.data);
      toast.success('Solicitud de recarga enviada');
    } catch(e) {
      toast.error(e.response?.data?.detail || 'Error al iniciar recarga');
    } finally { setLoading(false); }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  if (result) {
    const inst = result.instructions || {};
    return (
      <Modal title="Instrucciones de pago" onClose={() => { onClose(); onSuccess(); }}>
        <div style={{ textAlign:'center', marginBottom:20 }}>
          <div style={{ fontSize:40, marginBottom:8 }}>✅</div>
          <h3 style={{ fontWeight:800, fontSize:18, color:'#111827', marginBottom:4 }}>¡Solicitud creada!</h3>
          <p style={{ color:'#6b7280', fontSize:14 }}>Ref: <strong style={{ fontFamily:'monospace', color:'#f97316' }}>{result.reference}</strong></p>
          <div style={{ display:'flex', justifyContent:'center', gap:16, marginTop:12, flexWrap:'wrap' }}>
            <div style={{ textAlign:'center' }}><div style={{ fontSize:12, color:'#9ca3af' }}>Monto</div><div style={{ fontWeight:800, color:'#111827', fontSize:18 }}>{fmt(result.amount)}</div></div>
            <div style={{ textAlign:'center' }}><div style={{ fontSize:12, color:'#9ca3af' }}>Comisión</div><div style={{ fontWeight:700, color:'#ef4444', fontSize:18 }}>{fmt(result.fee)}</div></div>
            <div style={{ textAlign:'center' }}><div style={{ fontSize:12, color:'#9ca3af' }}>Recibes</div><div style={{ fontWeight:800, color:'#22c55e', fontSize:18 }}>{fmt(result.net_amount)}</div></div>
          </div>
        </div>

        {method === 'spei' && inst.clabe && (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[
              {label:'CLABE', value:inst.clabe},
              {label:'Banco', value:inst.bank},
              {label:'Beneficiario', value:inst.beneficiary},
              {label:'Referencia', value:inst.reference},
              {label:'Concepto', value:inst.concept},
            ].map(row => (
              <div key={row.label} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'#f9fafb', borderRadius:10 }}>
                <div><div style={{ fontSize:11, color:'#9ca3af', fontWeight:600 }}>{row.label}</div><div style={{ fontWeight:700, fontSize:14, fontFamily:'monospace' }}>{row.value}</div></div>
                <button onClick={()=>copy(row.value, row.label)} style={{ background:'transparent', border:'none', cursor:'pointer', color:'#9ca3af' }}>{copied===row.label?<Check size={14} color="#22c55e"/>:<Copy size={14}/>}</button>
              </div>
            ))}
          </div>
        )}

        {method === 'crypto' && inst.btc_address && (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[
              {label:'Bitcoin (BTC)', value:inst.btc_address},
              {label:'Ethereum (ETH)', value:inst.eth_address},
              {label:'USDT TRC-20', value:inst.usdt_trc20},
            ].map(row => (
              <div key={row.label} style={{ padding:'10px 14px', background:'#f9fafb', borderRadius:10 }}>
                <div style={{ fontSize:11, color:'#9ca3af', fontWeight:600, marginBottom:4 }}>{row.label}</div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontWeight:600, fontSize:11, fontFamily:'monospace', color:'#374151', wordBreak:'break-all' }}>{row.value}</span>
                  <button onClick={()=>copy(row.value, row.label)} style={{ background:'transparent', border:'none', cursor:'pointer', flexShrink:0 }}>{copied===row.label?<Check size={14} color="#22c55e"/>:<Copy size={14} color="#9ca3af"/>}</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {method === 'stripe' && <div style={{ textAlign:'center', padding:20, background:'#f5f3ff', borderRadius:12 }}><p style={{ color:'#6b7280', marginBottom:12 }}>Serás redirigido a Stripe para completar el pago de forma segura.</p><button style={{ padding:'12px 28px', background:'#6366f1', color:'#fff', border:'none', borderRadius:10, fontWeight:700, cursor:'pointer' }}>Pagar con Stripe →</button></div>}

        {method === 'cash' && inst.reference && (
          <div style={{ textAlign:'center', background:'#f0fdf4', borderRadius:12, padding:20 }}>
            <p style={{ color:'#15803d', marginBottom:8 }}>Código de referencia:</p>
            <p style={{ fontFamily:'monospace', fontSize:24, fontWeight:800, color:'#15803d' }}>{inst.reference}</p>
            <p style={{ color:'#6b7280', fontSize:13 }}>Válido por {inst.valid_for_hours} horas</p>
          </div>
        )}

        <button onClick={() => { onClose(); onSuccess(); }} style={{ width:'100%', marginTop:20, padding:'13px', background:'#f97316', color:'#fff', border:'none', borderRadius:12, fontWeight:700, cursor:'pointer' }}>Entendido</button>
      </Modal>
    );
  }

  return (
    <Modal title="💰 Recargar OkWallet" onClose={onClose}>
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
        <div>
          <p style={{ fontSize:13, fontWeight:700, color:'#374151', marginBottom:8 }}>Método de pago</p>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {METHODS.map(m => (
              <label key={m.id} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 16px', border:`2px solid ${method===m.id?'#f97316':'#e5e7eb'}`, borderRadius:12, cursor:'pointer', background:method===m.id?'#fff7ed':'#fff' }}>
                <input type="radio" name="method" checked={method===m.id} onChange={()=>setMethod(m.id)} style={{ accentColor:'#f97316' }} />
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>{m.icon}<span style={{ fontWeight:700, fontSize:13 }}>{m.label}</span></div>
                  <div style={{ fontSize:11, color:'#9ca3af', marginTop:2 }}>{m.desc}</div>
                </div>
                <span style={{ fontSize:11, fontWeight:800, color:method===m.id?'#f97316':'#9ca3af', flexShrink:0, background:method===m.id?'#fff7ed':'#f9fafb', padding:'2px 8px', borderRadius:20 }}>Comisión: {m.fee}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <p style={{ fontSize:13, fontWeight:700, color:'#374151', marginBottom:8 }}>Monto a recargar (MXN)</p>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:10 }}>
            {presets.map(p => (
              <button key={p} onClick={()=>setAmount(String(p))} style={{ padding:'7px 14px', border:`1.5px solid ${amount===String(p)?'#f97316':'#e5e7eb'}`, borderRadius:20, background:amount===String(p)?'#fff7ed':'#fff', fontWeight:700, fontSize:13, color:amount===String(p)?'#f97316':'#374151', cursor:'pointer' }}>${p}</button>
            ))}
          </div>
          <input type="number" value={amount} onChange={e=>setAmount(e.target.value)} placeholder="Otro monto..." min={50} style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #e5e7eb', borderRadius:12, fontSize:15, fontWeight:700, outline:'none', boxSizing:'border-box' }} />
        </div>

        <button disabled={!method || !amount || parseFloat(amount) <= 0 || loading} onClick={submit}
          style={{ width:'100%', padding:'14px', background:(!method||!amount||parseFloat(amount)<=0)?'#e5e7eb':'#f97316', color:'#fff', border:'none', borderRadius:12, fontWeight:800, fontSize:15, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
          {loading ? <><Loader2 size={18} className="animate-spin"/>Procesando...</> : `Recargar ${amount ? fmt(parseFloat(amount)) : ''}`}
        </button>
      </div>
    </Modal>
  );
}

const SEND_METHODS = [
  { id:'okwallet_id', label:'OkWallet ID', icon:'🪪', placeholder:'OKWrestaurante...', hint:'El código único que empieza con OKW' },
  { id:'nickname',   label:'Nickname',    icon:'👤', placeholder:'nombrerestaurante', hint:'Nombre del usuario / restaurante' },
  { id:'phone',      label:'WhatsApp',    icon:'📱', placeholder:'+521234567890',     hint:'Número con código de país' },
  { id:'qr',         label:'Código QR',   icon:'📷', placeholder:'',                 hint:'Escanea el QR del destinatario' },
];

function SendModal({ wallet, onClose, onSuccess }) {
  const [searchMethod, setSearchMethod] = useState('okwallet_id');
  const [searchValue, setSearchValue] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [amount, setAmount] = useState('');
  const [desc, setDesc] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const myOkWalletId = wallet?.okwallet_id || wallet?.id || '';

  const lookup = async () => {
    if (!searchValue.trim()) return;
    setLookupLoading(true);
    try {
      const res = await api.get(`/wallet/lookup/${encodeURIComponent(searchValue.trim())}`);
      setLookupResult(res.data);
      setStep(2);
    } catch(e) {
      toast.error(e.response?.data?.detail || 'Wallet no encontrada. Verifica el dato ingresado.');
    }
    finally { setLookupLoading(false); }
  };

  const send = async () => {
    if (!lookupResult || !amount || parseFloat(amount) <= 0) return;
    setLoading(true);
    try {
      const res = await api.post('/wallet/transfer', {
        to_wallet_id: lookupResult.wallet_id,
        amount: parseFloat(amount),
        description: desc,
      });
      toast.success(`Transferencia de ${fmt(res.data.amount)} completada`);
      onSuccess();
      onClose();
    } catch(e) {
      toast.error(e.response?.data?.detail || 'Error al transferir');
    } finally { setLoading(false); }
  };

  const activeMethod = SEND_METHODS.find(m => m.id === searchMethod);

  return (
    <Modal title="📤 Enviar pago" onClose={onClose} wide>
      {step === 1 && (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* Method tabs */}
          <div>
            <p style={{ color:'#6b7280', fontSize:12, margin:'0 0 10px', fontWeight:600 }}>¿Cómo quieres encontrar al destinatario?</p>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
              {SEND_METHODS.map(m => (
                <button key={m.id} onClick={() => { setSearchMethod(m.id); setSearchValue(''); setLookupResult(null); }}
                  style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, padding:'10px 6px', borderRadius:12,
                    border:`2px solid ${searchMethod===m.id?'#f97316':'#e5e7eb'}`,
                    background:searchMethod===m.id?'#fff7ed':'#fff',
                    cursor:'pointer', transition:'all 0.15s' }}>
                  <span style={{ fontSize:20 }}>{m.icon}</span>
                  <span style={{ fontSize:10, fontWeight:700, color:searchMethod===m.id?'#f97316':'#6b7280', textAlign:'center', lineHeight:1.2 }}>{m.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Search input — hidden for QR method */}
          {searchMethod !== 'qr' ? (
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>
                {activeMethod?.icon} Buscar por {activeMethod?.label}
              </label>
              <div style={{ display:'flex', gap:8 }}>
                <input value={searchValue} onChange={e=>setSearchValue(e.target.value)}
                  onKeyDown={e=>e.key==='Enter'&&lookup()}
                  placeholder={activeMethod?.placeholder}
                  style={{ flex:1, padding:'12px 14px', border:'1.5px solid #e5e7eb', borderRadius:12, fontSize:13, outline:'none', fontFamily: searchMethod==='okwallet_id'?'monospace':'inherit' }} />
                <button onClick={lookup} disabled={lookupLoading || !searchValue.trim()}
                  style={{ padding:'12px 18px', background:!searchValue.trim()?'#e5e7eb':'#f97316', color:'#fff', border:'none', borderRadius:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6, transition:'background 0.15s' }}>
                  {lookupLoading ? <Loader2 size={16} className="animate-spin"/> : <Search size={16}/>}
                </button>
              </div>
              <p style={{ fontSize:11, color:'#9ca3af', margin:'6px 0 0' }}>💡 {activeMethod?.hint}</p>
            </div>
          ) : (
            <div style={{ background:'#f8fafc', borderRadius:14, padding:'24px', textAlign:'center', border:'2px dashed #e2e8f0' }}>
              <div style={{ fontSize:48, marginBottom:8 }}>📷</div>
              <p style={{ fontWeight:700, color:'#374151', marginBottom:4 }}>Escanea el QR del destinatario</p>
              <p style={{ fontSize:12, color:'#9ca3af', marginBottom:16 }}>El código QR del destinatario contiene su OkWallet ID. Pídele que abra su OkWallet y te muestre su QR.</p>
              <p style={{ fontSize:12, color:'#6b7280' }}>O ingresa su OkWallet ID manualmente:</p>
              <button onClick={() => setSearchMethod('okwallet_id')} style={{ marginTop:8, padding:'8px 20px', background:'#f97316', color:'#fff', border:'none', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:13 }}>
                Ingresar OkWallet ID
              </button>
            </div>
          )}

          {/* My ID hint */}
          <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:14 }}>🪪</span>
            <div style={{ flex:1 }}>
              <p style={{ fontSize:11, color:'#0369a1', margin:0 }}>Tu OkWallet ID: <strong style={{ fontFamily:'monospace' }}>{myOkWalletId}</strong></p>
              <p style={{ fontSize:10, color:'#7dd3fc', margin:0 }}>Compártelo para recibir pagos directamente.</p>
            </div>
            <button onClick={() => { navigator.clipboard.writeText(myOkWalletId); toast.success('OkWallet ID copiado'); }}
              style={{ background:'transparent', border:'none', cursor:'pointer', color:'#0369a1' }}>
              <Copy size={13}/>
            </button>
          </div>
        </div>
      )}

      {step === 2 && lookupResult && (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:12, padding:'14px 18px', display:'flex', alignItems:'center', gap:12 }}>
            <CheckCircle2 size={22} color="#22c55e"/>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:800, fontSize:15, color:'#14532d' }}>
                {lookupResult.restaurant_name || lookupResult.restaurant_id}
              </div>
              <div style={{ fontSize:12, color:'#6b7280', display:'flex', gap:8, flexWrap:'wrap', marginTop:2 }}>
                <span>OkWallet ID: <strong style={{ fontFamily:'monospace', color:'#374151' }}>{lookupResult.okwallet_id || lookupResult.wallet_id}</strong></span>
                <span>· Tipo: {lookupResult.owner_type}</span>
              </div>
            </div>
          </div>

          <div>
            <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>Monto a enviar (MXN)</label>
            <input type="number" value={amount} onChange={e=>setAmount(e.target.value)} placeholder="0.00"
              style={{ width:'100%', padding:'13px 14px', border:'1.5px solid #e5e7eb', borderRadius:12, fontSize:20, fontWeight:800, outline:'none', boxSizing:'border-box' }} />
          </div>

          <div>
            <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>Descripción (opcional)</label>
            <input value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Pago de servicio..."
              style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #e5e7eb', borderRadius:12, fontSize:13, outline:'none', boxSizing:'border-box' }} />
          </div>

          <div style={{ background:'#fff7ed', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#92400e' }}>
            ⚠️ Se aplicará una comisión según el tipo de transferencia.
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={()=>{ setStep(1); setLookupResult(null); }}
              style={{ flex:1, padding:'12px', border:'1.5px solid #e5e7eb', borderRadius:12, background:'transparent', fontWeight:700, cursor:'pointer' }}>
              ← Volver
            </button>
            <button onClick={send} disabled={!amount||parseFloat(amount)<=0||loading}
              style={{ flex:2, padding:'12px', background:(!amount||parseFloat(amount)<=0)?'#e5e7eb':'#f97316', color:'#fff', border:'none', borderRadius:12, fontWeight:800, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              {loading ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}
              Enviar {amount ? fmt(parseFloat(amount)) : ''}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function ReceiveModal({ wallet, onClose }) {
  const [copied, setCopied] = useState(false);
  const okwalletId = wallet?.okwallet_id || wallet?.id || '';
  const copy = () => {
    navigator.clipboard.writeText(okwalletId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Modal title="📥 Recibir pagos" onClose={onClose}>
      <div style={{ textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:16 }}>
        <p style={{ color:'#6b7280', fontSize:13, maxWidth:320, margin:0 }}>Comparte tu <strong>OkWallet ID</strong> o escanea el código QR para recibir pagos de otros usuarios.</p>
        <div style={{ position:'relative', display:'inline-block' }}>
          <img src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(okwalletId)}&color=1e293b&bgcolor=ffffff`} alt="QR OkWallet" style={{ borderRadius:16, border:'3px solid #f97316', padding:10, background:'#fff', display:'block' }} />
          <div style={{ position:'absolute', bottom:-8, left:'50%', transform:'translateX(-50%)', background:'#f97316', color:'#fff', fontSize:9, fontWeight:800, padding:'2px 10px', borderRadius:20, whiteSpace:'nowrap', letterSpacing:0.5 }}>OkWallet ID</div>
        </div>
        <div style={{ background:'#f9fafb', borderRadius:12, padding:'14px 18px', width:'100%', boxSizing:'border-box' }}>
          <p style={{ fontSize:10, fontWeight:700, color:'#9ca3af', margin:'0 0 6px', textTransform:'uppercase', letterSpacing:1 }}>Tu OkWallet ID</p>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
            <span style={{ fontFamily:'monospace', fontSize:13, color:'#1e293b', fontWeight:700, wordBreak:'break-all' }}>{okwalletId}</span>
            <button onClick={copy} style={{ background:'#f97316', border:'none', borderRadius:8, padding:'6px 12px', cursor:'pointer', color:'#fff', flexShrink:0, display:'flex', alignItems:'center', gap:4, fontSize:11, fontWeight:700 }}>
              {copied ? <Check size={13}/> : <Copy size={13}/>} {copied ? 'Copiado' : 'Copiar'}
            </button>
          </div>
        </div>
        <p style={{ fontSize:12, color:'#9ca3af', margin:0 }}>Balance actual: <strong style={{ color:'#374151' }}>{fmt(wallet?.balance)}</strong></p>
        <div style={{ background:'#f0f9ff', borderRadius:10, padding:'10px 14px', width:'100%', boxSizing:'border-box' }}>
          <p style={{ fontSize:11, color:'#0369a1', margin:0 }}>💡 También pueden enviarte pagos por tu <strong>nickname</strong>, <strong>número de WhatsApp</strong>, o escaneando este QR.</p>
        </div>
      </div>
    </Modal>
  );
}

function DisputeModal({ tx, onClose, onSuccess }) {
  const [type, setType] = useState('');
  const [desc, setDesc] = useState('');
  const [loading, setLoading] = useState(false);
  const TYPES = [
    { id:'unauthorized', label:'Transacción no autorizada' },
    { id:'not_received', label:'Pago no recibido' },
    { id:'incorrect_amount', label:'Monto incorrecto' },
    { id:'other', label:'Otro motivo' },
  ];
  const submit = async () => {
    if (!type || desc.length < 10) return;
    setLoading(true);
    try {
      await api.post(`/wallet/transactions/${tx.id}/dispute`, { transaction_id: tx.id, dispute_type: type, description: desc });
      toast.success('Disputa registrada. Revisaremos en 24-72 horas.');
      onSuccess();
      onClose();
    } catch(e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };
  return (
    <Modal title="⚠️ Reportar transacción" onClose={onClose}>
      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
        <div style={{ background:'#fff7ed', borderRadius:10, padding:'12px 14px' }}>
          <div style={{ fontSize:12, color:'#92400e', fontWeight:700 }}>Transacción a disputar</div>
          <div style={{ fontSize:14, color:'#78350f', marginTop:2 }}>{tx.description} · {fmt(tx.amount)} · {fmtDate(tx.created_at)}</div>
        </div>
        <div>
          <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:8 }}>Motivo</label>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {TYPES.map(t=>(
              <label key={t.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', border:`1.5px solid ${type===t.id?'#f97316':'#e5e7eb'}`, borderRadius:10, cursor:'pointer', background:type===t.id?'#fff7ed':'#fff' }}>
                <input type="radio" checked={type===t.id} onChange={()=>setType(t.id)} style={{ accentColor:'#f97316' }} />
                <span style={{ fontSize:13, fontWeight:600 }}>{t.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>Descripción detallada *</label>
          <textarea value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Explica con detalle lo ocurrido..." rows={4} style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #e5e7eb', borderRadius:12, fontSize:13, resize:'none', outline:'none', boxSizing:'border-box' }} />
          <p style={{ fontSize:11, color:'#9ca3af', marginTop:4 }}>{desc.length} caracteres (mínimo 10)</p>
        </div>
        <button disabled={!type||desc.length<10||loading} onClick={submit}
          style={{ width:'100%', padding:'13px', background:(!type||desc.length<10)?'#e5e7eb':'#ef4444', color:'#fff', border:'none', borderRadius:12, fontWeight:800, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
          {loading ? <Loader2 size={16} className="animate-spin"/> : <AlertTriangle size={16}/>} Abrir disputa
        </button>
      </div>
    </Modal>
  );
}

// ─── Main Component ────────────────────────────
export default function OkWallet() {
  const { user } = useAuthStore();
  const [wallet, setWallet] = useState(null);
  const [txs, setTxs] = useState([]);
  const [totalTxs, setTotalTxs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [showBalance, setShowBalance] = useState(true);
  const [modal, setModal] = useState(null); // 'recharge' | 'send' | 'receive' | 'dispute'
  const [disputeTx, setDisputeTx] = useState(null);
  const [filterCat, setFilterCat] = useState('all');
  const [txPage, setTxPage] = useState(0);
  const PAGE_SIZE = 10;

  const fetchWallet = useCallback(async () => {
    try {
      const res = await api.get('/wallet/me');
      setWallet(res.data);
    } catch(e) { toast.error('Error al cargar wallet'); }
    finally { setLoading(false); }
  }, []);

  const fetchTxs = useCallback(async (page = 0, cat = 'all') => {
    setTxLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (cat !== 'all') params.category = cat;
      const res = await api.get('/wallet/transactions', { params });
      setTxs(res.data.transactions || []);
      setTotalTxs(res.data.total || 0);
    } catch {}
    finally { setTxLoading(false); }
  }, []);

  useEffect(() => { fetchWallet(); }, [fetchWallet]);
  useEffect(() => { fetchTxs(txPage, filterCat); }, [fetchTxs, txPage, filterCat]);

  const refresh = () => { fetchWallet(); fetchTxs(txPage, filterCat); };

  const CAT_FILTERS = [
    { id:'all', label:'Todos' },
    { id:'recharge', label:'Recargas' },
    { id:'transfer_in', label:'Recibidos' },
    { id:'transfer_out', label:'Enviados' },
    { id:'refund', label:'Reembolsos' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen">
        <TopBar title="OkWallet" />
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:400, gap:12 }}>
          <Loader2 size={24} color="#f97316" className="animate-spin"/>
          <span style={{ color:'#9ca3af' }}>Cargando tu wallet...</span>
        </div>
      </div>
    );
  }

  const pendingBalance = wallet?.pending_balance || 0;

  return (
    <div className="min-h-screen" style={{ fontFamily:"'Inter',sans-serif" }}>
      <TopBar title="OkWallet — Cartera Digital" />

      <div style={{ maxWidth:860, margin:'0 auto', padding:'24px 20px', display:'flex', flexDirection:'column', gap:24 }}>
        
        {/* ── Balance Card ── */}
        <div style={{ borderRadius:24, padding:'28px 28px 24px', background:'linear-gradient(135deg,#1e293b 0%,#0f172a 100%)', color:'#fff', position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', top:-40, right:-40, width:200, height:200, borderRadius:'50%', background:'rgba(249,115,22,0.15)' }}/>
          <div style={{ position:'absolute', bottom:-60, left:-20, width:160, height:160, borderRadius:'50%', background:'rgba(249,115,22,0.08)' }}/>
          <div style={{ position:'relative' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:40, height:40, borderRadius:12, background:'linear-gradient(135deg,#f97316,#fbbf24)', display:'flex', alignItems:'center', justifyContent:'center' }}><Wallet size={20}/></div>
                <div>
                  <p style={{ margin:0, fontSize:12, color:'rgba(255,255,255,0.6)', fontWeight:600 }}>OkWallet</p>
                  <p style={{ margin:0, fontSize:13, color:'rgba(255,255,255,0.8)' }}>{user?.restaurant_id || 'Mi Restaurante'}</p>
                </div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => setShowBalance(s=>!s)} style={{ background:'rgba(255,255,255,0.1)', border:'none', borderRadius:8, padding:'6px 8px', cursor:'pointer', color:'#fff', display:'flex' }}>
                  {showBalance ? <EyeOff size={15}/> : <Eye size={15}/>}
                </button>
                <button onClick={refresh} style={{ background:'rgba(255,255,255,0.1)', border:'none', borderRadius:8, padding:'6px 8px', cursor:'pointer', color:'#fff', display:'flex' }}><RefreshCw size={15}/></button>
              </div>
            </div>

            <div style={{ margin:'20px 0' }}>
              <p style={{ margin:0, fontSize:12, color:'rgba(255,255,255,0.5)', fontWeight:600, letterSpacing:1 }}>SALDO DISPONIBLE</p>
              <p style={{ margin:'4px 0 0', fontSize:42, fontWeight:900, letterSpacing:-1 }}>
                {showBalance ? fmt(wallet?.balance) : '••••••'}
              </p>
              {pendingBalance > 0 && (
                <p style={{ margin:'4px 0 0', fontSize:13, color:'rgba(249,115,22,0.8)' }}>
                  + {fmt(pendingBalance)} pendiente de acreditación
                </p>
              )}
            </div>

            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <div style={{ background:'rgba(255,255,255,0.08)', borderRadius:10, padding:'8px 14px', fontSize:12 }}>
                <span style={{ color:'rgba(255,255,255,0.5)' }}>Enviado: </span>
                <span style={{ fontWeight:700 }}>{showBalance ? fmt(wallet?.total_sent) : '••••'}</span>
              </div>
              <div style={{ background:'rgba(255,255,255,0.08)', borderRadius:10, padding:'8px 14px', fontSize:12 }}>
                <span style={{ color:'rgba(255,255,255,0.5)' }}>Recibido: </span>
                <span style={{ fontWeight:700 }}>{showBalance ? fmt(wallet?.total_received) : '••••'}</span>
              </div>
              <div style={{ background:'rgba(255,255,255,0.08)', borderRadius:10, padding:'8px 14px', fontSize:12 }}>
                <span style={{ color:'rgba(255,255,255,0.5)' }}>Estado: </span>
                <span style={{ fontWeight:700, color: wallet?.status === 'active' ? '#4ade80' : '#f87171', textTransform:'capitalize' }}>{wallet?.status}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Quick Actions ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
          {[
            { label:'Recargar', icon:<Plus size={22}/>, color:'#f97316', bg:'#fff7ed', action:'recharge' },
            { label:'Enviar', icon:<ArrowUpRight size={22}/>, color:'#8b5cf6', bg:'#f5f3ff', action:'send' },
            { label:'Recibir', icon:<ArrowDownLeft size={22}/>, color:'#22c55e', bg:'#f0fdf4', action:'receive' },
            { label:'Historial', icon:<History size={22}/>, color:'#06b6d4', bg:'#ecfeff', action:'history' },
          ].map(a => (
            <button key={a.action} onClick={() => a.action === 'history' ? document.getElementById('tx-history')?.scrollIntoView({behavior:'smooth'}) : setModal(a.action)}
              style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:10, padding:'18px 10px', background:a.bg, border:'none', borderRadius:16, cursor:'pointer', transition:'transform 0.1s' }}
              onMouseEnter={e=>e.currentTarget.style.transform='translateY(-2px)'}
              onMouseLeave={e=>e.currentTarget.style.transform='translateY(0)'}>
              <div style={{ width:48, height:48, borderRadius:14, background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', color:a.color, boxShadow:'0 2px 8px rgba(0,0,0,0.08)' }}>{a.icon}</div>
              <span style={{ fontSize:13, fontWeight:700, color:'#1e293b' }}>{a.label}</span>
            </button>
          ))}
        </div>

        {/* ── OkWallet ID Info ── */}
        <div style={{ background:'linear-gradient(135deg,#fff7ed,#fef3c7)', borderRadius:16, padding:'16px 20px', display:'flex', alignItems:'center', gap:12, border:'1.5px solid #fed7aa' }}>
          <div style={{ width:40, height:40, borderRadius:12, background:'#f97316', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <Shield size={18} color="#fff"/>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <p style={{ margin:0, fontSize:10, fontWeight:800, color:'#9a3412', textTransform:'uppercase', letterSpacing:1 }}>Tu OkWallet ID</p>
            <p style={{ margin:'2px 0 0', fontSize:13, fontFamily:'monospace', color:'#1e293b', fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {wallet?.okwallet_id || wallet?.id}
            </p>
          </div>
          <button onClick={() => { navigator.clipboard.writeText(wallet?.okwallet_id || wallet?.id || ''); toast.success('OkWallet ID copiado'); }}
            style={{ background:'#f97316', border:'none', borderRadius:10, padding:'8px 12px', cursor:'pointer', fontSize:11, fontWeight:800, color:'#fff', display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
            <Copy size={12}/> Copiar
          </button>
        </div>

        {/* ── Transaction History ── */}
        <div id="tx-history">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
            <h2 style={{ fontWeight:800, fontSize:18, margin:0, color:'#111827' }}>Historial de transacciones</h2>
            <span style={{ fontSize:12, color:'#9ca3af' }}>{totalTxs} transacciones</span>
          </div>

          {/* Category filter */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
            {CAT_FILTERS.map(f => (
              <button key={f.id} onClick={() => { setFilterCat(f.id); setTxPage(0); }}
                style={{ padding:'6px 14px', borderRadius:20, border:`1.5px solid ${filterCat===f.id?'#f97316':'#e5e7eb'}`, background:filterCat===f.id?'#fff7ed':'#fff', fontWeight:600, fontSize:12, color:filterCat===f.id?'#f97316':'#6b7280', cursor:'pointer' }}>
                {f.label}
              </button>
            ))}
          </div>

          {txLoading ? (
            <div style={{ display:'flex', justifyContent:'center', padding:40 }}><Loader2 size={24} color="#f97316" className="animate-spin"/></div>
          ) : txs.length === 0 ? (
            <div style={{ textAlign:'center', padding:'48px 20px', background:'#f9fafb', borderRadius:16 }}>
              <div style={{ fontSize:40, marginBottom:12 }}>💳</div>
              <p style={{ fontWeight:700, color:'#374151', marginBottom:4 }}>No hay transacciones</p>
              <p style={{ color:'#9ca3af', fontSize:13, marginBottom:16 }}>Recarga tu wallet para empezar.</p>
              <button onClick={() => setModal('recharge')} style={{ padding:'10px 24px', background:'#f97316', color:'#fff', border:'none', borderRadius:10, fontWeight:700, cursor:'pointer' }}>Recargar ahora</button>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {txs.map(tx => (
                <div key={tx.id} style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 18px', background:'#fff', border:'1px solid #f3f4f6', borderRadius:14, boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
                  <div style={{ width:44, height:44, borderRadius:12, background: tx.type==='credit'?'#f0fdf4':'#fff7ed', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>{categoryIcon(tx.category)}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{tx.description || tx.category}</div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:3, flexWrap:'wrap' }}>
                      <span style={{ fontSize:11, color:'#9ca3af' }}>{fmtDate(tx.created_at)}</span>
                      <span style={{ fontSize:11, fontFamily:'monospace', color:'#d1d5db' }}>·</span>
                      <span style={{ fontSize:11, color:'#9ca3af', fontFamily:'monospace' }}>{tx.reference}</span>
                      {statusBadge(tx.status)}
                    </div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontWeight:800, fontSize:16, color: tx.type==='credit'?'#22c55e':'#ef4444' }}>
                      {tx.type==='credit'?'+':'-'}{fmt(tx.net_amount || tx.amount)}
                    </div>
                    {tx.fee > 0 && <div style={{ fontSize:11, color:'#9ca3af' }}>Comisión: {fmt(tx.fee)}</div>}
                  </div>
                  {tx.status === 'completed' && tx.category !== 'fee' && tx.category !== 'adjustment' && (
                    <button onClick={() => { setDisputeTx(tx); setModal('dispute'); }} style={{ background:'transparent', border:'1px solid #fca5a5', borderRadius:8, padding:'5px 8px', cursor:'pointer', color:'#ef4444', flexShrink:0 }} title="Reportar">
                      <AlertTriangle size={13}/>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalTxs > PAGE_SIZE && (
            <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:16 }}>
              <button disabled={txPage === 0} onClick={() => setTxPage(p=>p-1)} style={{ padding:'8px 16px', border:'1px solid #e5e7eb', borderRadius:8, background:'#fff', cursor:txPage===0?'not-allowed':'pointer', fontWeight:600, fontSize:13, opacity:txPage===0?0.4:1 }}>← Anterior</button>
              <span style={{ padding:'8px 16px', fontSize:13, color:'#6b7280' }}>Pág. {txPage+1} de {Math.ceil(totalTxs/PAGE_SIZE)}</span>
              <button disabled={txPage >= Math.ceil(totalTxs/PAGE_SIZE)-1} onClick={() => setTxPage(p=>p+1)} style={{ padding:'8px 16px', border:'1px solid #e5e7eb', borderRadius:8, background:'#fff', cursor:'pointer', fontWeight:600, fontSize:13, opacity:txPage>=Math.ceil(totalTxs/PAGE_SIZE)-1?0.4:1 }}>Siguiente →</button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {modal === 'recharge' && <RechargeModal wallet={wallet} onClose={() => setModal(null)} onSuccess={refresh} />}
      {modal === 'send' && <SendModal wallet={wallet} onClose={() => setModal(null)} onSuccess={refresh} />}
      {modal === 'receive' && <ReceiveModal wallet={wallet} onClose={() => setModal(null)} />}
      {modal === 'dispute' && disputeTx && <DisputeModal tx={disputeTx} onClose={() => { setModal(null); setDisputeTx(null); }} onSuccess={refresh} />}
    </div>
  );
}
